import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

const Forgot = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch {
      // Anti-enumeration: same success state on error
      setDone(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2" data-testid="afri-forgot-page">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1591561954557-26941169b49e?auto=format&fit=crop&q=80&w=1200"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#1A2024]/40" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <h2 className="font-display text-4xl leading-tight">
            Forgot your password? It happens.
          </h2>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="font-display text-2xl mb-8 inline-block">
            AfriMarket
          </Link>
          <h1 className="font-display text-3xl mb-2">Reset your password</h1>
          <p className="text-muted-foreground text-sm mb-8">
            Enter the email you signed up with. We&apos;ll send you a secure one-time reset link.
          </p>

          {done ? (
            <div
              className="p-5 rounded-md border border-[#C05A3C]/30 bg-[#C05A3C]/5"
              data-testid="afri-forgot-success"
            >
              <div className="font-semibold text-base mb-2">Check your inbox</div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                If an account exists for <strong className="text-foreground">{email}</strong>,
                we&apos;ve emailed a password reset link. It&apos;s valid for 30 minutes.
              </p>
              <button
                type="button"
                onClick={() => navigate("/login")}
                data-testid="afri-forgot-back-to-login"
                className="mt-5 text-sm font-semibold text-[#C05A3C] hover:underline"
              >
                ← Back to login
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="afri-forgot-form">
              <div>
                <Label htmlFor="email" className="label-overline">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="afri-forgot-email"
                  className="mt-2"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="afri-forgot-submit"
                className="w-full h-12 bg-[#C05A3C] hover:bg-[#D46B4E] rounded-md"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Send reset link"
                )}
              </Button>
            </form>
          )}

          <p className="text-sm text-muted-foreground mt-6">
            Remembered it?{" "}
            <Link
              to="/login"
              className="text-[#C05A3C] font-medium hover:underline"
              data-testid="afri-forgot-login-link"
            >
              Back to login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Forgot;
